import { CloudDownload } from "lucide-react";
import React, { useContext, useEffect } from "react";
import { ActionIcon, Menu } from "@mantine/core";
import { Navigate } from "react-router-dom";
import { useDisclosure } from "@mantine/hooks";
import { HttpClientError } from "../../../providers/HttpClient";
import { AuthContext } from "../../../providers/AuthProvider";
import useDownloadEmployeeImportDataResultFileApiService from "../services/api/DownloadEmployeeImportDataResultFileApiService";
import useDownloadEmployeeDataFileApiService from "../services/api/DownloadEmployeeDataFileApiService";
import { useToasts } from "../../../providers/notifications/ToastsContext";

type DownloadEmployeeImportResultActionIconProps = {
  id: number;
};

const DownloadEmployeeDataFileActionIcon: React.FC<
  DownloadEmployeeImportResultActionIconProps
> = ({ id }) => {
  const auth = useContext(AuthContext);
  const { showToast } = useToasts();

  const [opened, handlers] = useDisclosure(false);

  const {
    isSuccess: isDownloadResultFileSuccess,
    isError: isDownloadResultFileError,
    mutate: downloadResultFile,
    error: downloadResultFileError,
    data: downloadResultFileResponse,
  } = useDownloadEmployeeImportDataResultFileApiService();

  const {
    isSuccess: isDownloadOriginalFileSuccess,
    isError: isDownloadOriginalFileError,
    mutate: downloadOriginalFile,
    error: downloadOriginalFileError,
    data: downloadOriginalFileResponse,
  } = useDownloadEmployeeDataFileApiService();

  useEffect(() => {
    if (isDownloadResultFileError || isDownloadOriginalFileError) {
      showToast({
        strapiId: "employee_data_import_file_dowload_failed",
        data: {
          message: HttpClientError(
            downloadResultFileError || downloadOriginalFileError
          ),
        },
      });
    }
  }, [
    isDownloadOriginalFileError,
    downloadOriginalFileError,
    isDownloadResultFileError,
    downloadOriginalFileError,
  ]);

  useEffect(() => {
    if (isDownloadOriginalFileSuccess && downloadOriginalFileResponse) {
      window.location.assign(downloadOriginalFileResponse.file_url);
      showToast({ strapiId: "employee_data_import_original_file_downloaded" });
    }

    if (isDownloadResultFileSuccess && downloadResultFileResponse) {
      window.location.assign(downloadResultFileResponse.file_url);
      showToast({ strapiId: "employee_data_import_result_file_downloaded" });
    }
  }, [
    downloadResultFileResponse,
    downloadOriginalFileResponse,
    isDownloadResultFileSuccess,
    isDownloadOriginalFileSuccess,
  ]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  return (
    <Menu
      opened={opened}
      onOpen={handlers.open}
      onClose={handlers.close}
      control={
        <ActionIcon onClick={() => handlers.toggle()}>
          <CloudDownload size={18} />
        </ActionIcon>
      }
    >
      <Menu.Item onClick={() => downloadOriginalFile(id)}>
        Download File
      </Menu.Item>
      <Menu.Item onClick={() => downloadResultFile(id)}>
        Download Results File
      </Menu.Item>
    </Menu>
  );
};

export default DownloadEmployeeDataFileActionIcon;
